// Considered: ItemCardMolecule — rejected. It renders a `ChannelItem` (item_metadata,
// item_version, is_syndicated, ratings, deploy button) — a `StudioTemplateCard` is an
// org-private template the caller can `view`, never a channel listing, so most of
// ItemCardMolecule's required fields would be fabricated placeholders. TemplateCardMolecule
// is bound to `TemplateApproach` (schema + approach creation flow), a different domain
// entirely. Built from canonical atoms directly instead — the same pattern
// `ComposerGalleryOrganism`'s card uses. See spec Design System Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import type { StudioTemplateCard, StudioTemplateFacts } from 'types/studio';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import ItemTypeGlyphAtom, { type MarketplaceItemType } from '../../atoms/marketplace/ItemTypeGlyphAtom';
import { MARKETPLACE_ITEM_TYPE_ICONS, MARKETPLACE_ITEM_TYPE_TONES } from '../../atoms/marketplace/iconMaps';
import TagAtom from '../../atoms/Tag/TagAtom';
import { StatusBadgeAtom } from '../../atoms/StatusBadge';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import { studioStatusTone } from './studioStatusTone';
import './StudioTemplateCardMolecule.scss';

export interface StudioTemplateCardMoleculeProps {
  card: StudioTemplateCard;
  /**
   * Channel count from the gallery's batched distribution lookup. `null` = lookup not yet
   * resolved or failed — renders no distribution chip at all (never a fabricated "Private";
   * the desk's anti-fabrication rule).
   */
  distributionChannelCount: number | null;
  onOpen: (card: StudioTemplateCard) => void;
}

function isMarketplaceItemType(type: string): type is MarketplaceItemType {
  return type in MARKETPLACE_ITEM_TYPE_ICONS;
}

function factsLineKey(facts: StudioTemplateFacts): { key: string; values: Record<string, number | string> } | null {
  switch (facts.kind) {
    case 'approach':
      return facts.templateFor ? { key: 'studio.gallery.facts.approach', values: { templateFor: facts.templateFor } } : null;
    case 'arc_template':
      return facts.stageCount != null ? { key: 'studio.gallery.facts.arcTemplate', values: { count: facts.stageCount } } : null;
    case 'aspect':
      return { key: 'studio.gallery.facts.aspect', values: { count: facts.propertyCount } };
    case 'schema':
      return facts.propertyCount != null ? { key: 'studio.gallery.facts.schema', values: { count: facts.propertyCount } } : null;
    case 'none':
    default:
      return null;
  }
}

/**
 * A single read-only card in a Studio browse-builder gallery (EAINT-873). `div[role="button"]` +
 * explicit Enter/Space handling — the same Sonar-noted pattern as `ComposerGalleryOrganism`'s
 * card — rather than a `<button>`, so future chip/badge descendants stay independently
 * interactive without nesting interactive elements.
 */
const StudioTemplateCardMolecule: React.FC<StudioTemplateCardMoleculeProps> = ({
  card,
  distributionChannelCount,
  onOpen,
}) => {
  const { t } = useTranslation();

  const marketplaceType: MarketplaceItemType = isMarketplaceItemType(card.type) ? card.type : 'composition';
  const facts = factsLineKey(card.facts);
  const statusTone = studioStatusTone(card.status);

  const handleOpen = () => onOpen(card);

  return (
    <div
      role="button"
      tabIndex={0}
      className="studio-template-card"
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        handleOpen();
      }}
      data-testid={`studio-gallery-card-${card.systemName}`}
    >
      <div className="studio-template-card__glyph">
        {card.glyph ? (
          // `template.glyph` is an Iconify `set:name` (or a letter) and `glyph_color` is a
          // structured GlyphColor — GlyphAtom is the canonical renderer for both. Rendering
          // the raw string in a tinted square would print `lucide:database` on screen.
          <GlyphAtom
            glyph={card.glyph}
            glyphColor={card.glyphColor ?? undefined}
            fallbackText={card.displayName}
            className="studio-template-card__glyph-icon"
            // Decorative: the display name beside it already names the card.
            aria-hidden
          />
        ) : (
          <ItemTypeGlyphAtom type={marketplaceType} size={40} />
        )}
      </div>

      <div className="studio-template-card__header">
        <span className="studio-template-card__name">{card.displayName}</span>
      </div>

      {card.description && <p className="studio-template-card__description">{card.description}</p>}

      {facts && <p className="studio-template-card__facts">{t(facts.key, facts.values)}</p>}

      <div className="studio-template-card__chips">
        <TagAtom
          label={t(`studio.desk.templateType.${card.type}`)}
          icon={MARKETPLACE_ITEM_TYPE_ICONS[marketplaceType]}
          tone={MARKETPLACE_ITEM_TYPE_TONES[marketplaceType]}
          size="small"
        />
        {statusTone && card.status && (
          <StatusBadgeAtom
            tone={statusTone}
            label={t(`studio.desk.status.${card.status}`)}
            size="small"
          />
        )}
        {distributionChannelCount !== null && (
          <ChipAtom
            label={
              distributionChannelCount > 0
                ? t('studio.gallery.distribution.listedIn', { count: distributionChannelCount })
                : t('studio.gallery.distribution.private')
            }
            icon={distributionChannelCount > 0 ? 'pi pi-share-alt' : 'pi pi-lock'}
            className="studio-template-card__distribution"
          />
        )}
      </div>
    </div>
  );
};

export default StudioTemplateCardMolecule;
